import { Button, Form, FormInstance, UploadFile, message } from 'antd';
import Upload, { UploadChangeParam } from 'antd/es/upload';
import { t } from 'i18next';
import fileDownload from 'js-file-download';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import ExtraTab from 'src/app/components/ExtraTabcomponent/ExtraTab';
import PageLayout from 'src/app/components/PageLayoutComponent/PageLayout';
import showModal from 'src/app/components/ShowModalComponent/ShowModal';
import TabLayout from 'src/app/components/TabLayoutComponent/TabLayout';
import { ICustomTabs } from 'src/app/components/TabLayoutComponent/tab-layout.config';
import TooltipWithIcon from 'src/app/components/TooltipWithIconComponent/TooltipWithIcon';
import { ExportInterestForm } from 'src/app/forms/ExportInterestForm/ExportInterestForm';
import GeneralReportForm from 'src/app/forms/GeneralReportForm/GeneralReportForm';
import StudentForm from 'src/app/forms/StudentForm/StudentForm';
import { formHelper } from 'src/app/helpers';
import { checkPermission } from 'src/app/helpers/permission.helper';
import usePermissions from 'src/app/hooks/usePermissions';
import Reports from 'src/app/pages/StudentsPage/Reports/Reports';
import styles from 'src/app/pages/StudentsPage/StudentsPage.module.scss';
import { ConfigContentStateType } from 'src/app/redux/configContent/configContentSlice';
import { useGetStudentsQuery, useImportStudentMutation } from 'src/app/redux/students/studentsSlice';
import { getFile } from 'src/app/services/storage-server-service/storage.service';
import { routes } from 'src/configs/permissions.dictionary';
import { GeneralReportType } from 'src/enums/general-report.enum';
import { DownloadIcon } from 'src/icons';
import AddInteractionIcon from 'src/icons/AddInteractionIcon';
import ExportIcon from 'src/icons/ExportIcon';
import ImportIcon from 'src/icons/ImportIcon';

// Lazy Imports
const LazyStudentsTable = React.lazy(() => import('./StudentsTable/StudentsTable'));

const StudentsPage = () => {
  const [filteredTableData, setFilteredTableData] = useState<[]>([]);
  const { permissions } = usePermissions();

  const { data: studentsList, refetch: refetchStudents } = useGetStudentsQuery();

  const [studentForm] = Form.useForm();
  const [exportForm] = Form.useForm();
  const [studentReportForm] = Form.useForm();

  const [importStudent] = useImportStudentMutation();

  const currentTheme: ConfigContentStateType = useSelector((state: any) => state.configContent);

  const addStudent = (form: FormInstance) => (
    <div className="flex-center" style={{ columnGap: '50px' }}>
      <div
        className="extra-tab-content"
        onClick={(event) =>
          showModal({
            event,
            title: t('studentsPage.addStudent'),
            size: 'medium-2',
            content: <StudentForm form={form} />,
            disableCancelButton: true,
            okText: 'Create',
            onSave: (modal) => formHelper.submitForm(studentForm, modal),
            onCancel: () => studentForm.resetFields(),
          })
        }
      >
        <AddInteractionIcon color={currentTheme.theme.colors.quoteBgc} />
        <Button className="bordered-tab-button">
          <span className={styles.headerText}>{t('studentsPage.addStudent')}</span>
        </Button>
      </div>
    </div>
  );

  const extraContent = (isGraduated?: boolean) => {
    const archiveOrAllStudents = isGraduated ? 'Archive' : 'AllStudent';
    const exportPermissions = checkPermission(permissions, [routes['/Students'], archiveOrAllStudents, 'export']);
    const importPermissions = checkPermission(permissions, [routes['/Students'], archiveOrAllStudents, 'upload']);
    const addPermissions = checkPermission(permissions, [routes['/Students'], archiveOrAllStudents, 'add']);

    return (
      <div className={styles.studentBtns}>
        <div className={styles.icons}>
          <TooltipWithIcon title="Download Template">
            <DownloadIcon onClick={onDownloadTemplate} />
          </TooltipWithIcon>
          {importPermissions && (
            <Upload
              className={styles.uploadWrapper}
              beforeUpload={() => false}
              itemRender={undefined}
              onChange={onFileImport}
              maxCount={1}
              showUploadList={false}
            >
              <TooltipWithIcon title={t('global.import')}>
                <ImportIcon />
              </TooltipWithIcon>
            </Upload>
          )}

          {exportPermissions && (
            <TooltipWithIcon title={t('global.export')}>
              <ExportIcon onClick={onFileExport} />
            </TooltipWithIcon>
          )}
          {addPermissions && !isGraduated && addStudent(studentForm)}
        </div>
      </div>
    );
  };
  const onFileImport = (value: UploadChangeParam<UploadFile<any>>) => {
    const importFile = value.fileList[0].originFileObj;
    if (!importFile) return;
    importStudent({ file: importFile })
      .unwrap()
      .then(() => {
        message.success('File imported successfully');
        refetchStudents();
      })
      .catch(() => message.error('Invalid file type'));
  };

  const onFileExport = (event: any) => {
    showModal({
      event,
      size: 'medium',
      title: 'Export Students',
      content: <ExportInterestForm form={exportForm} filteredInterestIds={filteredTableData} interestName="student" />,
      disableCancelButton: true,
      onSave: (modal) => formHelper.submitForm(exportForm, modal),
    });
  };

  const onDownloadTemplate = async () => {
    try {
      const { data } = await getFile({
        path: `app_resources/Students-Template.xlsx`,
      });
      if (data) fileDownload(data, `Students-Template.xlsx`);
    } catch (error) {
      console.log('error', error);
    }
  };

  const totalStudentCount = studentsList?.rows.length ? studentsList.rows.length : 0;
  const tabItems: ICustomTabs[] = [
    {
      key: '1',
      label: <Button>{t('tables.students.students.tabName')}</Button>,
      children: (
        <LazyStudentsTable
          graduatedOrNot={false}
          infoTotalCount={totalStudentCount}
          setFilteredTableData={setFilteredTableData}
        />
      ),
      extendedContents: extraContent(),
      disabled: !checkPermission(permissions, [routes['/Students'], 'AllStudent']),
    },
    {
      key: '2',
      label: <Button>{t('tables.students.archived.tabName')}</Button>,
      children: (
        <LazyStudentsTable
          isArchived
          graduatedOrNot
          infoTotalCount={totalStudentCount}
          setFilteredTableData={setFilteredTableData}
        />
      ),
      extendedContents: extraContent(true),
      disabled: !checkPermission(permissions, [routes['/Students'], 'Archive']),
    },
    {
      key: '3',
      label: <Button>{t('tables.students.reports.tabName')}</Button>,
      children: <Reports />,
      extendedContents: (
        <ExtraTab
          icon={<AddInteractionIcon />}
          text="Create Report"
          iconTheme
          onClick={(event) => {
            showModal({
              event,
              size: 'medium-2',
              title: 'Create Report',
              content: <GeneralReportForm form={studentReportForm} type={GeneralReportType.Student} />,
              onSave: (modal) => formHelper.submitForm(studentReportForm, modal),
            });
          }}
        />
      ),
    },
  ];

  return (
    <PageLayout id="students-page" title={t('sidebar.students')}>
      <TabLayout paramName="subTab" items={tabItems} className="tabs-with-border-container" />
    </PageLayout>
  );
};

export default StudentsPage;
